import { Integration } from '@sentry/core';
import * as Sentry from '@sentry/react';
import { DummyMonitoring, MonitoringInstance } from '@xometry/ui';
import { ENV } from 'common/constants/env';

import {
  isBogusUserAgentEvent,
  isBrowserInjectedErrorEvent,
  isHandledGraphQLError,
  isHandledNetworkError,
  isHoopsLostWebGLContextErrorEvent,
  isUnauthorizedSentryEvent,
} from './monitoring.utils';

const CUSTOM_FUNNEL_NAME_PREFIX = '___';
const DEFAULT_TRACES_SAMPLE_RATE = ENV.ENV_CUSTOM_ENV === 'production' ? 0.1 : 1;
/** Scripts the Facebook / Instagram in-app browser injects into every page it opens. */
const IN_APP_BROWSER_SCRIPT_URL = /^iabjs:\/\//;
/** Scripts browser extensions inject into the page (crypto wallets, translators, ad blockers). */
const BROWSER_EXTENSION_SCRIPT_URL = /^(?:chrome|moz|ms-browser|safari(?:-web)?)-extension:\/\//;

export const getMonitoringClient = (): Promise<MonitoringInstance> =>
  import('./Sentry')
    .then(
      (r) =>
        new r.SentryMonitoring({
          production: ENV.ENV_CUSTOM_ENV === 'production',
          environment: ENV.ENV_CUSTOM_ENV,
          release: ENV.RELEASE,
          dsn: ENV.SENTRY_DSN,
          configure: (draft) => {
            const integrations = (
              Array.isArray(draft.integrations) ? [...draft.integrations] : [draft.integrations]
            ) as Integration[];

            integrations.push(
              // https://docs.sentry.io/platforms/javascript/session-replay/privacy/#privacy-configuration
              Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: true,
                networkDetailAllowUrls: [`${ENV.API_ENDPOINT}/partners/graphql`],
              }),
            );

            const { beforeSend } = draft;

            return {
              ...draft,
              beforeSend: (...args) => {
                const [event, hint] = args;

                // An expired session fails every in-flight operation with a 401 at once — noise, not a bug.
                if (isUnauthorizedSentryEvent(event, hint)) {
                  return null;
                }

                // The error link has already notified the user and reported (or deliberately skipped) it.
                if (isHandledGraphQLError(hint?.originalException)) {
                  return null;
                }

                // The request got no response (offline, a page unloading mid-request); the error link told the user.
                if (isHandledNetworkError(hint?.originalException)) {
                  return null;
                }

                // A browser's own injected script failed (Chrome on iOS), nothing in this app to fix.
                if (isBrowserInjectedErrorEvent(event)) {
                  return null;
                }

                // The 3D viewer's engine lost its WebGL context on the user's machine, nothing in this app to fix.
                if (isHoopsLostWebGLContextErrorEvent(event)) {
                  return null;
                }

                // A crawler with a made-up User-Agent (Baiduspider's renderer says `pc`) broke something, not a user.
                if (isBogusUserAgentEvent(event)) {
                  return null;
                }

                return beforeSend ? beforeSend(...args) : event;
              },
              // The messenger micro-frontend runs inside this page but is owned and triaged elsewhere, the
              // Facebook in-app browser's own scripts throw once the app tears down its WebView, and browser
              // extensions throw on their own (two wallets fighting over `window.ethereum`).
              // Sentry matches the frame that threw, so our own code called from either still reports.
              denyUrls: [
                ...(draft.denyUrls ?? []),
                IN_APP_BROWSER_SCRIPT_URL,
                BROWSER_EXTENSION_SCRIPT_URL,
                ...(ENV.MSG_ENDPOINT ? [ENV.MSG_ENDPOINT] : []),
              ],
              dist: draft.release,
              integrations: integrations as typeof draft.integrations,
              tracePropagationTargets: [ENV.API_ENDPOINT],
              tracesSampler: (samplingContext) => {
                if (samplingContext.name?.startsWith(CUSTOM_FUNNEL_NAME_PREFIX)) {
                  return 1;
                }

                return samplingContext.inheritOrSampleWith(DEFAULT_TRACES_SAMPLE_RATE);
              },
            };
          },
        }),
    )
    .catch(() => new DummyMonitoring());
